import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Card,
  Switch,
  Table,
  message,
  Steps,
  Checkbox,
  Progress,
  Tag,
  Space,
} from "antd";
import {
  CalculatorOutlined,
  PrinterOutlined,
  SaveOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  RightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import { TClient } from "~/types";
import { calculateCubicVolume, getClientName } from "~/utils";
import { StorageInvoicePrintContent } from "./storage-invoice/storage-invoice-print-content";
import { useCreateStorageInvoice } from "~/services/storage/storage.queries";
import { StorageInvoiceLineItem } from "~/services/storage/storage.api";

interface BatchInvoiceModalProps {
  visible: boolean;
  onClose: () => void;
  allClients: TClient[];
}

interface LocationCharge {
  locationId: string;
  locationName: string;
  itemCount: number;
  actualCubicFt: number;
  minimumCubicFt: number;
  billableCubicFt: number;
  ratePerCubicFt: number;
  amount: number;
}

interface InvoiceLineItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface StorageInvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  billingPeriod: string;
  client: TClient;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  notes: string;
}

type BatchStep = "select" | "review";

export const BatchInvoiceModal: React.FC<BatchInvoiceModalProps> = ({
  visible,
  onClose,
  allClients,
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState<BatchStep>("select");

  // Selection step state
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);

  // Review step state
  const [currentClientIndex, setCurrentClientIndex] = useState(0);
  const [completedClients, setCompletedClients] = useState<Set<string>>(
    new Set()
  );
  const [skippedClients, setSkippedClients] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Current client invoice state
  const [locationCharges, setLocationCharges] = useState<LocationCharge[]>([]);
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [insuranceRate, setInsuranceRate] = useState(0.01);
  const [inventoryValue, setInventoryValue] = useState(0);

  // Create invoice mutation
  const createInvoiceMutation = useCreateStorageInvoice();

  // Print ref
  const printRef = useRef<HTMLDivElement>(null);

  // Get selected clients in order
  const selectedClients = useMemo(() => {
    return allClients.filter((c) => selectedClientIds.includes(c.id));
  }, [allClients, selectedClientIds]);

  // Current client being reviewed
  const currentClient = selectedClients[currentClientIndex] || null;

  // Calculated totals
  const storageTotal = useMemo(
    () => locationCharges.reduce((sum, loc) => sum + loc.amount, 0),
    [locationCharges]
  );

  const insuranceAmount = useMemo(
    () => (includeInsurance ? inventoryValue * insuranceRate : 0),
    [includeInsurance, inventoryValue, insuranceRate]
  );

  const totalAmount = useMemo(
    () => storageTotal + insuranceAmount,
    [storageTotal, insuranceAmount]
  );

  // Generate invoice data for printing
  const invoiceData: StorageInvoiceData | null = useMemo(() => {
    if (!currentClient || locationCharges.length === 0) return null;

    const lineItems: InvoiceLineItem[] = locationCharges.map((loc) => ({
      description: `Storage - ${loc.locationName} (${loc.billableCubicFt.toFixed(2)} ft³ @ $${loc.ratePerCubicFt.toFixed(2)}/ft³)${loc.billableCubicFt > loc.actualCubicFt ? " [min applied]" : ""}`,
      quantity: 1,
      rate: loc.ratePerCubicFt,
      amount: loc.amount,
    }));

    if (includeInsurance && insuranceAmount > 0) {
      lineItems.push({
        description: `Storage Insurance (${(insuranceRate * 100).toFixed(2)}% of $${inventoryValue.toFixed(2)} inventory value)`,
        quantity: 1,
        rate: insuranceAmount,
        amount: insuranceAmount,
      });
    }

    return {
      invoiceNumber: `${currentClient.clientRef || currentClient.id.slice(0, 6).toUpperCase()}_${selectedMonth.format("MMMM")}_${selectedMonth.format("YYYY")}_ArtStorage`,
      invoiceDate: dayjs().format("MM/DD/YYYY"),
      billingPeriod: selectedMonth.format("MMMM YYYY"),
      client: currentClient,
      lineItems,
      subtotal: totalAmount,
      taxRate: 0,
      taxAmount: 0,
      total: totalAmount,
      notes:
        "Payment due upon receipt. Thank you for storing with Art Handler Collective.",
    };
  }, [
    currentClient,
    selectedMonth,
    locationCharges,
    includeInsurance,
    insuranceRate,
    inventoryValue,
    insuranceAmount,
    totalAmount,
  ]);

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: invoiceData
      ? `Storage_Invoice_${invoiceData.invoiceNumber}`
      : "Storage_Invoice",
  });

  // Calculate storage for current client
  const calculateForClient = (client: TClient) => {
    const inventory = client.inventoryClient || [];
    const parentItems = inventory.filter((inv) => !inv.parentInventoryId);

    // Group by location
    const byLocation = parentItems.reduce(
      (acc, item) => {
        const locationId = item.location?.id || "no-location";
        if (!acc[locationId]) {
          acc[locationId] = {
            location: item.location,
            items: [],
          };
        }
        acc[locationId].items.push(item);
        return acc;
      },
      {} as Record<string, { location?: any; items: any[] }>
    );

    // Calculate charges per location
    const charges: LocationCharge[] = Object.entries(byLocation).map(
      ([locationId, { location, items }]) => {
        const actualCubicFt = items.reduce((sum, item) => {
          return sum + calculateCubicVolume(item) / 1728;
        }, 0);

        const minimumCubicFt = Number(location?.minimumCubicFeet) || 0;
        const billableCubicFt = Math.max(actualCubicFt, minimumCubicFt);
        const ratePerCubicFt = Number(location?.ratePerCubicFt) || 0;
        const amount = billableCubicFt * ratePerCubicFt;

        return {
          locationId,
          locationName: location?.name || "No Location",
          itemCount: items.length,
          actualCubicFt: Math.round(actualCubicFt * 100) / 100,
          minimumCubicFt,
          billableCubicFt: Math.round(billableCubicFt * 100) / 100,
          ratePerCubicFt,
          amount: Math.round(amount * 100) / 100,
        };
      }
    );

    // Calculate total inventory value
    const totalValue = parentItems.reduce((acc, item) => {
      return acc + (Number(item.value) || 0);
    }, 0);

    setLocationCharges(charges);
    setInventoryValue(totalValue);
    setIncludeInsurance(false);
    setInsuranceRate(0.01);
  };

  // Reset when modal opens
  useEffect(() => {
    if (visible) {
      setCurrentStep("select");
      setSelectedMonth(dayjs());
      setSelectedClientIds([]);
      setCurrentClientIndex(0);
      setCompletedClients(new Set());
      setSkippedClients(new Set());
      setLocationCharges([]);
    }
  }, [visible]);

  // Calculate when current client changes
  useEffect(() => {
    if (currentStep === "review" && currentClient) {
      calculateForClient(currentClient);
    }
  }, [currentStep, currentClientIndex, currentClient]);

  // Update location charge field
  const updateLocationCharge = (
    locationId: string,
    field: keyof LocationCharge,
    value: number
  ) => {
    setLocationCharges((prev) =>
      prev.map((loc) => {
        if (loc.locationId !== locationId) return loc;

        const updated = { ...loc, [field]: value };

        if (field === "actualCubicFt" || field === "minimumCubicFt") {
          updated.billableCubicFt = Math.max(
            updated.actualCubicFt,
            updated.minimumCubicFt
          );
          updated.amount =
            Math.round(updated.billableCubicFt * updated.ratePerCubicFt * 100) /
            100;
        } else if (field === "billableCubicFt" || field === "ratePerCubicFt") {
          updated.amount =
            Math.round(updated.billableCubicFt * updated.ratePerCubicFt * 100) /
            100;
        }

        return updated;
      })
    );
  };

  // Start review process
  const handleStartReview = () => {
    if (selectedClientIds.length === 0) {
      message.warning("Please select at least one client");
      return;
    }
    setCurrentClientIndex(0);
    setCurrentStep("review");
  };

  // Save current invoice and move to next
  const handleSaveAndNext = async () => {
    if (!currentClient || !invoiceData) return;

    setIsSaving(true);

    try {
      const lineItems: StorageInvoiceLineItem[] = locationCharges.map(
        (loc) => ({
          locationId:
            loc.locationId !== "no-location" ? loc.locationId : undefined,
          locationName: loc.locationName,
          description: `Storage - ${loc.locationName} (${loc.billableCubicFt.toFixed(2)} ft³ @ $${loc.ratePerCubicFt.toFixed(2)}/ft³)${loc.billableCubicFt > loc.actualCubicFt ? " [min applied]" : ""}`,
          actualCubicFt: loc.actualCubicFt,
          minimumCubicFt: loc.minimumCubicFt,
          billableCubicFt: loc.billableCubicFt,
          ratePerCubicFt: loc.ratePerCubicFt,
          amount: loc.amount,
          type: "storage" as const,
        })
      );

      if (includeInsurance && insuranceAmount > 0) {
        lineItems.push({
          locationName: "Insurance",
          description: `Storage Insurance (${(insuranceRate * 100).toFixed(2)}% of $${inventoryValue.toFixed(2)} inventory value)`,
          amount: insuranceAmount,
          type: "insurance" as const,
        });
      }

      await createInvoiceMutation.mutateAsync({
        clientId: currentClient.id,
        billingPeriodMonth: selectedMonth.month() + 1,
        billingPeriodYear: selectedMonth.year(),
        invoiceDate: dayjs().toISOString(),
        lineItems,
        storageTotal,
        insuranceTotal: insuranceAmount,
        totalAmount,
      });

      // Mark as completed
      setCompletedClients((prev) => new Set([...prev, currentClient.id]));

      message.success(
        `Invoice saved for ${currentClient.companyName || getClientName(currentClient)}`
      );

      // Move to next client
      if (currentClientIndex < selectedClients.length - 1) {
        setCurrentClientIndex((prev) => prev + 1);
      } else {
        message.success("All invoices completed!");
        onClose();
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      message.error("Failed to save invoice");
    } finally {
      setIsSaving(false);
    }
  };

  // Skip current client
  const handleSkip = () => {
    if (!currentClient) return;

    setSkippedClients((prev) => new Set([...prev, currentClient.id]));

    if (currentClientIndex < selectedClients.length - 1) {
      setCurrentClientIndex((prev) => prev + 1);
    } else {
      message.info("Review complete");
      onClose();
    }
  };

  // Select all clients
  const handleSelectAll = () => {
    setSelectedClientIds(allClients.map((c) => c.id));
  };

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedClientIds([]);
  };

  // Location columns
  const locationColumns = [
    {
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      render: (name: string) => (
        <span className="flex items-center gap-1">
          <EnvironmentOutlined className="text-gray-400" />
          {name}
        </span>
      ),
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 60,
      align: "center" as const,
    },
    {
      title: "Actual ft³",
      dataIndex: "actualCubicFt",
      key: "actualCubicFt",
      width: 100,
      render: (value: number, record: LocationCharge) => (
        <InputNumber
          size="small"
          value={value}
          onChange={(v) =>
            updateLocationCharge(record.locationId, "actualCubicFt", v || 0)
          }
          min={0}
          precision={2}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "Min",
      dataIndex: "minimumCubicFt",
      key: "minimumCubicFt",
      width: 80,
      render: (value: number, record: LocationCharge) => (
        <InputNumber
          size="small"
          value={value}
          onChange={(v) =>
            updateLocationCharge(record.locationId, "minimumCubicFt", v || 0)
          }
          min={0}
          precision={0}
          style={{ width: 70 }}
        />
      ),
    },
    {
      title: "Billable",
      dataIndex: "billableCubicFt",
      key: "billableCubicFt",
      width: 80,
      render: (value: number, record: LocationCharge) => (
        <span
          className={
            value > record.actualCubicFt
              ? "text-orange-600 font-medium"
              : "font-medium"
          }
        >
          {value.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Rate",
      dataIndex: "ratePerCubicFt",
      key: "ratePerCubicFt",
      width: 90,
      render: (value: number, record: LocationCharge) => (
        <InputNumber
          size="small"
          value={value}
          onChange={(v) =>
            updateLocationCharge(record.locationId, "ratePerCubicFt", v || 0)
          }
          min={0}
          precision={2}
          prefix="$"
          style={{ width: 75 }}
        />
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 90,
      align: "right" as const,
      render: (value: number) => (
        <span className="font-medium">${value.toFixed(2)}</span>
      ),
    },
  ];

  // Progress info
  const progressPercent =
    selectedClients.length > 0
      ? Math.round(
          ((completedClients.size + skippedClients.size) /
            selectedClients.length) *
            100
        )
      : 0;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <TeamOutlined />
          <span>Batch Invoice Creation</span>
        </div>
      }
      open={visible}
      width={currentStep === "select" ? 700 : 950}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {/* Selection Step */}
      {currentStep === "select" && (
        <div className="space-y-4">
          <Card size="small" title="Billing Period">
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(date) => date && setSelectedMonth(date)}
              format="MMMM YYYY"
              style={{ width: 200 }}
            />
          </Card>

          <Card
            size="small"
            title={
              <div className="flex justify-between items-center">
                <span>Select Clients ({selectedClientIds.length} selected)</span>
                <Space>
                  <Button size="small" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button size="small" onClick={handleDeselectAll}>
                    Clear
                  </Button>
                </Space>
              </div>
            }
          >
            <div
              style={{ maxHeight: 400, overflowY: "auto" }}
              className="space-y-1"
            >
              {allClients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  onClick={() => {
                    setSelectedClientIds((prev) =>
                      prev.includes(client.id)
                        ? prev.filter((id) => id !== client.id)
                        : [...prev, client.id]
                    );
                  }}
                >
                  <Checkbox checked={selectedClientIds.includes(client.id)} />
                  <span className="flex-1">
                    {client.companyName || getClientName(client)}
                  </span>
                  <span className="text-gray-400 text-sm">
                    {client.inventoryClient?.filter((i) => !i.parentInventoryId)
                      .length || 0}{" "}
                    items
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<RightOutlined />}
              onClick={handleStartReview}
              disabled={selectedClientIds.length === 0}
            >
              Start Review ({selectedClientIds.length} clients)
            </Button>
          </div>
        </div>
      )}

      {/* Review Step */}
      {currentStep === "review" && currentClient && (
        <div className="space-y-4">
          {/* Progress Header */}
          <Card size="small">
            <div className="flex items-center gap-4">
              <Progress
                type="circle"
                percent={progressPercent}
                size={50}
                format={() =>
                  `${currentClientIndex + 1}/${selectedClients.length}`
                }
              />
              <div className="flex-1">
                <div className="text-lg font-medium">
                  {currentClient.companyName || getClientName(currentClient)}
                </div>
                <div className="text-sm text-gray-500">
                  {selectedMonth.format("MMMM YYYY")} •{" "}
                  {completedClients.size} saved, {skippedClients.size} skipped
                </div>
              </div>
              <div className="flex gap-1">
                {selectedClients.map((c, idx) => (
                  <div
                    key={c.id}
                    className={`w-3 h-3 rounded-full ${
                      completedClients.has(c.id)
                        ? "bg-green-500"
                        : skippedClients.has(c.id)
                          ? "bg-gray-300"
                          : idx === currentClientIndex
                            ? "bg-blue-500"
                            : "bg-gray-200"
                    }`}
                    title={c.companyName || getClientName(c)}
                  />
                ))}
              </div>
            </div>
          </Card>

          {/* Location Charges */}
          <Card
            size="small"
            title={
              <span className="flex items-center gap-2">
                <EnvironmentOutlined />
                Storage Charges by Location
              </span>
            }
          >
            <Table
              dataSource={locationCharges}
              columns={locationColumns}
              rowKey="locationId"
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row className="bg-gray-50 font-medium">
                    <Table.Summary.Cell index={0} colSpan={4}>
                      Storage Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      {locationCharges
                        .reduce((sum, loc) => sum + loc.billableCubicFt, 0)
                        .toFixed(2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      ${storageTotal.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>

          {/* Insurance */}
          <Card size="small" title="Insurance">
            <div className="flex items-center gap-4">
              <Switch checked={includeInsurance} onChange={setIncludeInsurance} />
              <span>Include Insurance</span>
              {includeInsurance && (
                <>
                  <span className="text-gray-400">|</span>
                  <span className="text-sm">
                    ${insuranceAmount.toFixed(2)} ({(insuranceRate * 100).toFixed(1)}% of $
                    {inventoryValue.toFixed(2)})
                  </span>
                </>
              )}
            </div>
          </Card>

          {/* Total */}
          <Card size="small" className="bg-blue-50">
            <div className="flex justify-between text-lg font-bold">
              <span>Total:</span>
              <span>${totalAmount.toFixed(2)}</span>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button onClick={() => setCurrentStep("select")}>
              Back to Selection
            </Button>
            <div className="flex gap-2">
              <Button onClick={handleSkip}>Skip</Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => handlePrint()}
                disabled={!invoiceData}
              >
                Print
              </Button>
              <Button
                type="primary"
                icon={
                  currentClientIndex < selectedClients.length - 1 ? (
                    <SaveOutlined />
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
                onClick={handleSaveAndNext}
                loading={isSaving}
              >
                {currentClientIndex < selectedClients.length - 1
                  ? "Save & Next"
                  : "Save & Finish"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden print content */}
      {invoiceData && (
        <StorageInvoicePrintContent ref={printRef} invoiceData={invoiceData} />
      )}
    </Modal>
  );
};
